import { useEffect, useMemo, useState } from 'react';

import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { getArticles } from '../../services/firebase';
import { filterArticleList } from '../../services/articleSearch';
import type { Article } from '../../types';

/**
 * Pick the article a landing page links to.
 *
 * The field this replaces took a raw Firestore doc id, which meant a curator
 * had to go and find the id in another tab before they could link anything.
 * Articles have no Algolia index and the collection is small, so the whole list
 * is fetched once per open and filtered in memory — the same shape the
 * entity-link picker already uses, sharing its match rule via
 * `filterArticleList`. Single-select: a collection carries one `articleId`.
 */

const RESULT_LIMIT = 20;

interface ArticlePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The picked article's doc id, which is what `CuratedCollection.articleId` holds. */
  onSelect: (article: Article) => void;
  /** Opens with this article ticked, when the collection already links one. */
  selectedId?: string;
}

export function ArticlePickerModal({
  isOpen,
  onClose,
  onSelect,
  selectedId,
}: ArticlePickerModalProps) {
  const [articles, setArticles] = useState<Article[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [pickedId, setPickedId] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setPickedId(selectedId || '');
  }, [isOpen, selectedId]);

  useEffect(() => {
    if (!isOpen || articles) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getArticles()
      .then((list) => {
        if (!cancelled) setArticles(list);
      })
      .catch((err) => {
        console.error('Failed to load articles:', err);
        if (!cancelled) setError('Could not load articles.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, articles]);

  const rows = useMemo(
    () => filterArticleList(articles || [], query).slice(0, RESULT_LIMIT),
    [articles, query],
  );

  const picked = useMemo(
    () => (articles || []).find((a) => a.id === pickedId) || null,
    [articles, pickedId],
  );

  const handleSelect = () => {
    if (!picked) return;
    onSelect(picked);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Choose an article" size="xl">
      <div className="space-y-4">
        <input
          type="text"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search articles by title or category…"
          aria-label="Search articles"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="border border-gray-200 rounded-lg divide-y max-h-[26rem] overflow-y-auto">
          {loading && (
            <p className="px-3 py-8 text-sm text-gray-500 text-center">
              Loading articles…
            </p>
          )}
          {!loading && rows.length === 0 && !error && (
            <p className="px-3 py-8 text-sm text-gray-500 text-center">
              No matching articles.
            </p>
          )}
          {rows.map((article) => (
            <ArticleRow
              key={article.id}
              article={article}
              picked={article.id === pickedId}
              onPick={() => setPickedId(article.id)}
            />
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSelect} disabled={!picked}>
            Select article
          </Button>
        </div>
      </div>
    </Modal>
  );
}

interface ArticleRowProps {
  article: Article;
  picked: boolean;
  onPick: () => void;
}

function ArticleRow({ article, picked, onPick }: ArticleRowProps) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={picked}
      className={`w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-gray-50 ${
        picked ? 'bg-brand-50' : ''
      }`}
    >
      <input
        type="radio"
        checked={picked}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        className="shrink-0"
      />
      <ArticleThumbnail url={article.thumbnailUrl} alt={article.title} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-gray-900 truncate">
          {article.title || article.id}
        </div>
        <div className="text-xs text-gray-500 truncate">
          {articleSubtitle(article)}
        </div>
      </div>
      {!article.enabled && (
        <Badge variant="default">Disabled</Badge>
      )}
    </button>
  );
}

/** Author · published date · category, skipping whatever the article lacks. */
export function articleSubtitle(article: Article): string {
  const parts: string[] = [];
  if (article.author?.name) parts.push(article.author.name);
  const published = formatPublishedDate(article);
  if (published) parts.push(published);
  if (article.category) parts.push(article.category);
  return parts.join(' · ');
}

/**
 * `publishedDate` is a Firestore `Timestamp` on every article the admin writes,
 * but a hand-seeded doc can hold a plain string or nothing at all, and a picker
 * row is no place to throw over it.
 */
export function formatPublishedDate(article: Article): string {
  const value = article.publishedDate as unknown;
  if (!value) return '';
  if (
    typeof value === 'object' &&
    'toDate' in (value as Record<string, unknown>) &&
    typeof (value as { toDate: unknown }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate().toLocaleDateString();
  }
  const parsed = new Date(value as string | number);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString();
}

interface ArticleThumbnailProps {
  url: string | undefined;
  alt: string;
  className?: string;
}

/** Article thumbnails are absolute URLs, so no re-signing — just a fallback. */
export function ArticleThumbnail({
  url,
  alt,
  className = 'w-14 h-14',
}: ArticleThumbnailProps) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [url]);

  if (!url || failed) {
    return (
      <div
        className={`${className} shrink-0 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] text-gray-400 text-center leading-tight`}
      >
        No image
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={alt}
      onError={() => setFailed(true)}
      className={`${className} shrink-0 rounded object-cover bg-gray-100 border border-gray-200`}
    />
  );
}
